import { Injectable } from '@angular/core';
import { AES, enc, SHA256 } from 'crypto-js';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class EncryptionService {
  // Use environment variable for encryption key
  // In production, this should be stored securely, not in code
  private readonly encryptionKey: string;

  constructor() {
    // Get key from environment or use a fallback for development
    // IMPORTANT: In production, use environment variables, not hardcoded values
    this.encryptionKey = environment.encryptionKey || 'IMMOVEA_DEFAULT_KEY_CHANGE_IN_PRODUCTION';
  }

  /**
   * Encrypts sensitive data using AES encryption
   * @param data The data to encrypt
   * @returns Encrypted string
   */
  encrypt(data: string): string {
    if (!data) return '';
    
    try {
      const encrypted = AES.encrypt(data, this.encryptionKey).toString();
      return encrypted;
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypts encrypted data
   * @param encryptedData The encrypted string to decrypt
   * @returns Decrypted string
   */
  decrypt(encryptedData: string): string {
    if (!encryptedData) return '';
    
    try {
      const bytes = AES.decrypt(encryptedData, this.encryptionKey);
      const decrypted = bytes.toString(enc.Utf8);
      return decrypted;
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  /**
   * Hashes data using SHA256 (one-way, cannot be decrypted)
   * Useful for comparing values without storing original
   * @param data The data to hash
   * @returns SHA256 hash string
   */
  hash(data: string): string {
    if (!data) return '';
    
    try {
      return SHA256(data).toString();
    } catch (error) {
      console.error('Hashing error:', error);
      throw new Error('Failed to hash data');
    }
  }

  /**
   * Encrypts an object by encrypting its sensitive fields
   * @param obj Object containing data to encrypt
   * @param fieldsToEncrypt Array of field names to encrypt
   * @returns Object with encrypted fields
   */
  encryptObject<T extends Record<string, any>>(
    obj: T, 
    fieldsToEncrypt: (keyof T)[]
  ): T {
    const encrypted = { ...obj };
    
    fieldsToEncrypt.forEach(field => {
      if (encrypted[field] && typeof encrypted[field] === 'string') {
        encrypted[field] = this.encrypt(encrypted[field] as string) as any;
      }
    });
    
    return encrypted;
  }

  /**
   * Decrypts an object by decrypting its encrypted fields
   * @param obj Object containing encrypted data
   * @param fieldsToDecrypt Array of field names to decrypt
   * @returns Object with decrypted fields
   */
  decryptObject<T extends Record<string, any>>(
    obj: T, 
    fieldsToDecrypt: (keyof T)[]
  ): T {
    const decrypted = { ...obj };
    
    fieldsToDecrypt.forEach(field => {
      if (decrypted[field] && typeof decrypted[field] === 'string') {
        try {
          decrypted[field] = this.decrypt(decrypted[field] as string) as any;
        } catch (error) {
          console.error(`Failed to decrypt field: ${String(field)}`);
          // Keep encrypted value if decryption fails
        }
      }
    });
    
    return decrypted;
  }
}
